import type { ApolloClient } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import checkVersion, { isServerVersionCompatible } from './checkVersion';
import type { useAppStore } from './redux/useAppSelector';
import authTokenUtil from '../account/authTokenUtil';
import localLogout from '../account/localLogout';
import visitorAuthTokenUtil from '../account/visitorAuthTokenUtil';
import getNetworkErrorExtra from '../utils/error/getNetworkErrorExtra';
import Logger from '../utils/Logger';
import { isPublicViewPath } from '../utils/useIsPublicView';

const LOGGING_IGNORED_QUERIES = ['currentVersion'];

function handleUnauthorizedError() {
  const isPublicOrEmbedView =
    isPublicViewPath(window.location.pathname) ||
    window.location.pathname.startsWith('/embed/');

  if (isPublicOrEmbedView) {
    const hasAuthToken = !!authTokenUtil.get();
    const hasVisitorAuthToken = !!visitorAuthTokenUtil.get();
    const isVisitorSession = !hasAuthToken && hasVisitorAuthToken;

    if (isVisitorSession) {
      visitorAuthTokenUtil.remove();
    } else {
      localLogout({
        preventRedirect: true,
      });
    }
    // we only want to reload if we either had an authToken or visitorAuthToken before
    // otherwise, we might end up in an endless reload loop
    if (hasAuthToken || hasVisitorAuthToken) {
      window.location.reload();
    }
  } else {
    localLogout();
  }
}

export type ValidationErrorHandler = () => Promise<{
  isVersionMismatchModalOpen: boolean;
}>;

interface GetErrorLinkOptions {
  getClient: () => ApolloClient<object>;
  store: ReturnType<typeof useAppStore>;
}

const getErrorLink = ({ getClient, store }: GetErrorLinkOptions) =>
  onError((error) => {
    const { graphQLErrors, networkError } = error;

    if (graphQLErrors?.find((e) => e.message === 'UNAUTHORIZED')) {
      handleUnauthorizedError();
      return;
    }

    const { operationName, query, variables } =
      'operation' in error
        ? error.operation
        : { operationName: null, query: null, variables: null };

    if (operationName && LOGGING_IGNORED_QUERIES.includes(operationName)) {
      return;
    }

    const definition = query?.definitions[0];
    const operationType =
      definition && 'operation' in definition ? definition.operation : '';
    const operationMessage = operationName
      ? `: ${operationType} ${operationName}`
      : '';

    const slabResponseExtensions = error.response?.extensions?.slab as
      | { version: string }
      | undefined;

    if (graphQLErrors) {
      graphQLErrors
        .filter(
          (e) => !('code' in e) || typeof e.code !== 'number' || e.code >= 500,
        )
        .forEach((e) => {
          (async () => {
            let isVersionMismatchModalOpen = false;

            if (
              slabResponseExtensions?.version &&
              !isServerVersionCompatible(slabResponseExtensions.version)
            ) {
              try {
                const result = await checkVersion({
                  client: getClient(),
                  store,
                });
                isVersionMismatchModalOpen = result.isVersionMismatchModalOpen;
              } catch (validationHandlerError) {
                Logger.error(validationHandlerError.message, {
                  originalError: e.message,
                });
              }
            }

            const log = `${e.message}${operationMessage}`;

            Logger.warn(
              `[Apollo GQL] ${log}`,
              {
                code: 'code' in e ? e.code : undefined,
                variables,
              },
              {
                fingerprint: log,
                tags: {
                  isVersionMismatchModalOpen,
                },
              },
            );
          })();
        });
    }

    if (networkError && 'statusCode' in networkError) {
      if (
        networkError.statusCode === 401 &&
        'result' in networkError &&
        typeof networkError.result !== 'string' &&
        networkError.result.message === 'invalid_token'
      ) {
        handleUnauthorizedError();
        return;
      }

      if (networkError.statusCode >= 500) {
        const log = `${networkError.statusCode} ${networkError.message}${operationMessage}`;

        Logger.warn(
          `[Apollo Network] ${log}`,
          {
            ...getNetworkErrorExtra(networkError),
            variables,
          },
          { fingerprint: log },
        );
      }
    }
  });

export default getErrorLink;
